import { useState, useCallback, useRef, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, FileText, BookOpen, MessageSquare, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { PDFViewer } from '@/components/ui/PDFViewer';
import { BriefEditor } from './BriefEditor';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { attachmentService, priceBookBriefService, type PriceBookBrief } from '@/services';
import { VALIDATOR_SYSTEM_PROMPT } from '@/config/briefingPrompt';

interface DomainKnowledgeTabProps {
  priceBookId: string;
  companyId: string;
  userId: string;
  systemPrompt: string;
}

export function DomainKnowledgeTab({ priceBookId, companyId, userId, systemPrompt }: DomainKnowledgeTabProps) {
  const queryClient = useQueryClient();
  const [innerTab, setInnerTab] = useState('terms');
  const [rightTab, setRightTab] = useState('briefing');
  const briefSnapshotRef = useRef<PriceBookBrief | null>(null);

  // Query terms attachment
  const { data: termsAttachments = [], isLoading: termsLoading } = useQuery({
    queryKey: ['price-book-terms', priceBookId],
    queryFn: async () => {
      const result = await attachmentService.listByEntityWithRole('price_book', priceBookId, 'terms');
      if (result.error) throw result.error;
      return result.data;
    },
  });

  // Get signed URL for the first terms attachment
  const { data: signedUrl, isLoading: urlLoading } = useQuery({
    queryKey: ['price-book-terms-url', termsAttachments[0]?.file_path],
    queryFn: async () => {
      if (!termsAttachments[0]) return null;
      const { url, error } = await attachmentService.getSignedUrl(termsAttachments[0].file_path);
      if (error) throw error;
      return url;
    },
    enabled: termsAttachments.length > 0,
  });

  // Query brief
  const { data: brief, isLoading: briefLoading } = useQuery({
    queryKey: ['price-book-brief', priceBookId],
    queryFn: async () => {
      const result = await priceBookBriefService.getByPriceBook(priceBookId);
      if (result.error) throw result.error;
      if (result.data) briefSnapshotRef.current = result.data;
      return result.data;
    },
  });

  const validatorSystemPrompt = useMemo(() => {
    const briefContent = brief?.brief_content
      ? brief.brief_content
      : '(No Domain Knowledge Document has been created yet. Answer based on the price book PDF only.)';
    return VALIDATOR_SYSTEM_PROMPT + '\n\n## Domain Knowledge Document\n\n' + briefContent;
  }, [brief?.brief_content]);

  const handleSave = useCallback(async (html: string) => {
    const context = { companyId, userId };

    if (brief) {
      // Update existing brief content
      const snapshot = briefSnapshotRef.current || brief;
      const result = await priceBookBriefService.updateContent(brief.id, html, snapshot, context);
      if (result.error) {
        toast.error('Failed to save brief');
        throw result.error;
      }
      // Update snapshot for next diff
      if (result.data) briefSnapshotRef.current = result.data;
      queryClient.setQueryData(['price-book-brief', priceBookId], result.data);
    } else {
      // Create new brief via upsert
      const result = await priceBookBriefService.upsert(priceBookId, { brief_content: html }, context);
      if (result.error) {
        toast.error('Failed to create brief');
        throw result.error;
      }
      if (result.data) briefSnapshotRef.current = result.data;
      queryClient.setQueryData(['price-book-brief', priceBookId], result.data);
    }
  }, [brief, priceBookId, companyId, userId, queryClient]);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" style={{ height: '700px' }}>
      <ResizablePanelGroup direction="horizontal">
        <ResizablePanel defaultSize={65} minSize={40} className="min-w-0 overflow-hidden">
          <div className="flex flex-col h-full overflow-hidden">
            <Tabs value={innerTab} onValueChange={setInnerTab} className="flex flex-col h-full">
              <TabsList className="w-full justify-start rounded-none border-b bg-muted/30 px-2 h-10 flex-shrink-0">
                <TabsTrigger value="terms" className="gap-1.5 text-xs">
                  <FileText className="w-3.5 h-3.5" />
                  Price Book Terms
                </TabsTrigger>
                <TabsTrigger value="document" className="gap-1.5 text-xs">
                  <BookOpen className="w-3.5 h-3.5" />
                  Domain Knowledge Document
                </TabsTrigger>
              </TabsList>

              <TabsContent value="terms" className="flex-1 m-0 overflow-hidden">
                {termsLoading || urlLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : signedUrl ? (
                  <PDFViewer url={signedUrl} className="h-full" />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2 px-8">
                    <FileText className="w-10 h-10 opacity-40" />
                    <p className="text-sm text-center">
                      No price book terms PDF found. Go to the Attachments tab and upload a PDF document with the role "terms".
                    </p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="document" className="flex-1 m-0 p-3 overflow-hidden">
                {briefLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <BriefEditor
                    content={brief?.brief_content || ''}
                    onSave={handleSave}
                  />
                )}
              </TabsContent>
            </Tabs>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={35} minSize={20}>
          <Tabs value={rightTab} onValueChange={setRightTab} className="flex flex-col h-full">
            <TabsList className="w-full justify-start rounded-none border-b bg-muted/30 px-2 h-10 flex-shrink-0">
              <TabsTrigger value="briefing" className="gap-1.5 text-xs">
                <MessageSquare className="w-3.5 h-3.5" />
                Briefing Assistant
              </TabsTrigger>
              <TabsTrigger value="validation" className="gap-1.5 text-xs">
                <ShieldCheck className="w-3.5 h-3.5" />
                Validation Assistant
              </TabsTrigger>
            </TabsList>

            <TabsContent value="briefing" className="flex-1 m-0 overflow-hidden">
              <ChatPanel
                entityType="price_book"
                entityId={priceBookId}
                companyId={companyId}
                userId={userId}
                systemPrompt={systemPrompt}
                attachmentFilePath={termsAttachments[0]?.file_path}
              />
            </TabsContent>

            <TabsContent value="validation" className="flex-1 m-0 overflow-hidden">
              <ChatPanel
                entityType="price_book"
                entityId={priceBookId}
                companyId={companyId}
                userId={userId}
                systemPrompt={validatorSystemPrompt}
                attachmentFilePath={termsAttachments[0]?.file_path}
                sessionType="validation"
                title="Validation Assistant"
                emptyStateText="Ask pricing validation questions against this price book."
                icon={ShieldCheck}
              />
            </TabsContent>
          </Tabs>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
